/**
 * Shared payment (הנה"ח וכספים → תשלומים) types.
 *
 * A payment is **one customer's monthly account**: the sum of every order shipped to them in
 * one calendar month, less the credits raised for that same month. It is produced by the
 * monthly billing job on the last day of the Gregorian month, never by hand.
 *
 * Two payment routes share one row shape and one charge date:
 * - `PAYMENT_VIA_ACCOUNT` (מל"ש) — the row is opened and settled off-platform, so it ends at
 *   `PAYMENT_STATUS_TO_ACCOUNT` and no clearing call is made.
 * - `PAYMENT_BY_CARD` (אשראי) — the row is charged through Cardcom with the token saved on the
 *   customer's subscription, and ends at `שולם` / `נכשל` / `ממתין לאמצעי תשלום`.
 *
 * Like an order and a credit, a payment **snapshots** what it displays (`customerName`,
 * `paymentMethod`, `cardLast4`), so renaming a customer or switching their payment method
 * later never rewrites a month that has already been billed.
 *
 * This file is dependency-free (no React/MUI/browser imports) so the backend can import it
 * via the `@malash-office/types/payments` deep alias.
 */
import { SelectOptionLite } from './customer.types';
import { PAYMENT_BY_CARD, PAYMENT_VIA_ACCOUNT } from './subscription.types';

const toOptions = (values: readonly string[]): SelectOptionLite[] => values.map(v => ({ label: v, value: v }));

// --- סטטוס תשלום ---
// As everywhere else in this project, the stored value *is* the Hebrew label.
/** Row created, not yet routed — only visible mid-run or after a crash. */
export const PAYMENT_STATUS_PENDING = 'ממתין לחיוב';
/** מל"ש route, terminal: the sum is owed on the מל"ש account and collected off-platform. */
export const PAYMENT_STATUS_TO_ACCOUNT = 'לחיוב בחשבון מל"ש';
/** Card route, terminal: Cardcom accepted the charge. */
export const PAYMENT_STATUS_CHARGED = 'שולם';
/** Card route: Cardcom rejected the charge. Retryable. */
export const PAYMENT_STATUS_FAILED = 'נכשל';
/** Card route: the customer has no saved card token, so there is nothing to charge. */
export const PAYMENT_STATUS_NO_METHOD = 'ממתין לאמצעי תשלום';
/**
 * Card route: a charge was sent to Cardcom and we never learned the outcome — the process died
 * between the request and the write. The money may well have been taken.
 *
 * The billing job must **never** charge one of these again on its own. It is cleared by a
 * person who checked Cardcom: either retry deliberately, or cancel the row.
 */
export const PAYMENT_STATUS_IN_DOUBT = 'בבירור מול קארדקום';
/** Terminal: credits covered the whole month, so nothing is owed. */
export const PAYMENT_STATUS_NOTHING_DUE = 'אין חיוב';
/** Terminal: voided by an admin. */
export const PAYMENT_STATUS_CANCELLED = 'בוטל';

export const PAYMENT_STATUSES = [
  PAYMENT_STATUS_PENDING,
  PAYMENT_STATUS_TO_ACCOUNT,
  PAYMENT_STATUS_CHARGED,
  PAYMENT_STATUS_FAILED,
  PAYMENT_STATUS_NO_METHOD,
  PAYMENT_STATUS_IN_DOUBT,
  PAYMENT_STATUS_NOTHING_DUE,
  PAYMENT_STATUS_CANCELLED
] as const;

export const PAYMENT_STATUS_OPTIONS = toOptions(PAYMENT_STATUSES);

/** Chip colour per status, as **theme palette keys** — this project never hardcodes a colour. */
export const PAYMENT_STATUS_COLOR: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  [PAYMENT_STATUS_PENDING]: 'default',
  [PAYMENT_STATUS_TO_ACCOUNT]: 'info',
  [PAYMENT_STATUS_CHARGED]: 'success',
  [PAYMENT_STATUS_FAILED]: 'error',
  [PAYMENT_STATUS_NO_METHOD]: 'warning',
  [PAYMENT_STATUS_IN_DOUBT]: 'warning',
  [PAYMENT_STATUS_NOTHING_DUE]: 'default',
  [PAYMENT_STATUS_CANCELLED]: 'error'
};

/**
 * Statuses the billing job must not charge on its own — either the month is settled, or the
 * outcome of a previous attempt is unknown and only a person who checked Cardcom may decide.
 */
export const NO_AUTO_CHARGE_STATUSES: readonly string[] = [
  PAYMENT_STATUS_CHARGED,
  PAYMENT_STATUS_TO_ACCOUNT,
  PAYMENT_STATUS_NOTHING_DUE,
  PAYMENT_STATUS_CANCELLED,
  PAYMENT_STATUS_IN_DOUBT
];

/** A month that has been dealt with. Re-pricing or re-charging one of these is always wrong. */
export const SETTLED_STATUSES: readonly string[] = [
  PAYMENT_STATUS_CHARGED,
  PAYMENT_STATUS_TO_ACCOUNT,
  PAYMENT_STATUS_NOTHING_DUE,
  PAYMENT_STATUS_CANCELLED
];

/**
 * How many times the nightly sweep will re-present a declined card before giving up, matching
 * the existing subscriptions engine. A card that has failed five nights running is not going to
 * succeed on the sixth — it needs somebody to talk to the customer, not another attempt.
 */
export const MAX_CHARGE_ATTEMPTS = 5;

/**
 * Whether the nightly sweep may present this card again.
 *
 * A row `בבירור מול קארדקום` is deliberately excluded: its outcome is unknown, so retrying it
 * automatically could take the money twice. That one waits for a person, which is the only case
 * left that a person has to touch.
 */
export const isAutoRetryable = (
  payment: Pick<Payment, 'status' | 'paymentMethod' | 'attempts'>
): boolean =>
  payment.paymentMethod === PAYMENT_BY_CARD &&
  payment.status === PAYMENT_STATUS_FAILED &&
  payment.attempts < MAX_CHARGE_ATTEMPTS;

// --- שיטת התשלום ---
// Not redefined here: the source of truth is `PAYMENT_VIA_ACCOUNT` / `PAYMENT_BY_CARD` in
// subscription.types.ts, and a payment row is a snapshot of the subscription's own method.
// They are not re-exported either — `libs/types/src/index.ts` re-exports both files, and a
// duplicate export would be ambiguous.

/** Short labels for the table chip — the stored values are full sentences. */
export const PAYMENT_METHOD_SHORT_LABEL: Record<string, string> = {
  [PAYMENT_VIA_ACCOUNT]: 'חשבון מל"ש',
  [PAYMENT_BY_CARD]: 'כרטיס אשראי'
};

export const PAYMENT_METHOD_FILTER_OPTIONS: SelectOptionLite[] = [
  { label: PAYMENT_METHOD_SHORT_LABEL[PAYMENT_VIA_ACCOUNT], value: PAYMENT_VIA_ACCOUNT },
  { label: PAYMENT_METHOD_SHORT_LABEL[PAYMENT_BY_CARD], value: PAYMENT_BY_CARD }
];

export interface Payment {
  id: number;
  /** 'YYYY-MM' — the month being billed. */
  period: string;
  /** Kept for traceability and for the breakdown; the display reads `customerName`. */
  customerId: number | null;
  /** Snapshot of the customer name at the moment the month was billed. */
  customerName: string;
  /** Snapshot: one of `PAYMENT_VIA_ACCOUNT` / `PAYMENT_BY_CARD`. */
  paymentMethod: string;
  /**
   * The window of shipping dates this charge covers, inclusive 'YYYY-MM-DD'. A cycle is not a
   * calendar month — מל"ש runs 27th-to-26th and each card standing order runs on its own
   * anniversary — so the row states its window rather than leaving it to be inferred.
   */
  periodStart: string;
  periodEnd: string;
  /** 'YYYY-MM-DD' — the day the charge was raised; the day after `periodEnd`. */
  chargeDate: string;
  /** How many orders make up `grossAmount`. */
  ordersCount: number;
  /** Σ over the month's orders of (quantity × unitPrice). */
  itemsAmount: number;
  /** Σ over the month's orders of the per-order delivery cost. */
  deliveryAmount: number;
  /** `itemsAmount + deliveryAmount`, before credits. */
  grossAmount: number;
  /** Positive: how much of the month's valid credits this row absorbed. */
  creditsAmount: number;
  /** `max(0, grossAmount - creditsAmount)` — the sum actually charged. */
  amount: number;
  status: string;
  /** The subscription whose card token was used (card route only). */
  subscriptionId: number | null;
  cardLast4: string | null;
  providerTransactionId: string | null;
  receiptUrl: string | null;
  failureReason: string | null;
  /** How many times a charge has been attempted on this row. */
  attempts: number;
  chargedAt?: string | null;
  runId: number | null;
  createdAt?: string;
  updatedAt?: string;
}

/** One order line of the breakdown behind a payment row. */
export interface PaymentBreakdownOrder {
  id: number;
  /** 'YYYY-MM-DD' */
  orderDate: string;
  planName: string;
  status: string;
  itemsAmount: number;
  deliveryCost: number;
  total: number;
}

/** One credit line of the breakdown behind a payment row. */
export interface PaymentBreakdownCredit {
  id: number;
  /** 'YYYY-MM-DD' */
  creditDate: string;
  planName: string | null;
  reason: string;
  amount: number;
}

/** What the expanded row shows: the orders that add up, and the credits that come off. */
export interface PaymentBreakdown {
  paymentId: number;
  orders: PaymentBreakdownOrder[];
  credits: PaymentBreakdownCredit[];
}

// --- ייצוא לפריוריטי ---

/**
 * One `4` line of a Priority order — a single מק"ט the customer actually received.
 *
 * Priority bills by catalogue line, not by monthly total: a customer holding two subscriptions
 * gets one line per מק"ט under a single customer header, never one merged line.
 */
export interface PriorityExportLine {
  /** `null` when the catalogue item was deleted, or when it carries no מק"ט yet. */
  sku: string | null;
  /** Snapshot of the item name taken from the order line, so a later rename never restates it. */
  name: string;
  quantity: number;
  unitPrice: number;
}

/**
 * One customer's charge, resolved into everything a Priority order needs.
 *
 * `deliveryAmount` and `creditsAmount` stay separate from `lines` because neither is a
 * catalogue item: each becomes its own `4` line under a fixed מק"ט, and the credit one is
 * negative so the order total lands on `amount` — what was really collected.
 */
export interface PriorityExportRow {
  paymentId: number;
  /** Lets the export dialog link a blocked row to the customer card, where the number is entered. */
  customerId: number | null;
  customerName: string;
  /** `Customer.priorityId` — the מספר לקוח. `null` blocks the row from being exported. */
  priorityId: string | null;
  /** One of `PAYMENT_VIA_ACCOUNT` / `PAYMENT_BY_CARD`. */
  paymentMethod: string;
  period: string;
  /** Net — `max(0, gross - credits)`. The order's lines must add up to exactly this. */
  amount: number;
  deliveryAmount: number;
  /**
   * Positive. Already capped at the gross, because `amount` is floored at zero: without the
   * cap an over-credited month would export a negative order that was never charged.
   */
  creditsAmount: number;
  lines: PriorityExportLine[];
}

/** The export endpoint's answer: the rows, and how many card charges were left out of them. */
export interface PriorityExportResponse {
  rows: PriorityExportRow[];
  /** Card charges the filters matched. They are never exported — Cardcom issued their invoice. */
  excludedCardCount: number;
}

// --- ריצות חיוב ---
export const BILLING_MODE_AUTOMATIC = 'אוטומטי';
export const BILLING_MODE_MANUAL = 'ידני';

/** One execution of the nightly billing job — the audit trail for "why does this row exist". */
export interface PaymentRun {
  id: number;
  /** 'YYYY-MM' of the run date. */
  period: string;
  runAt: string;
  mode: string;
  dryRun: boolean;
  /** Customer/method groups found for the period. */
  examined: number;
  /**
   * Rows this run *opened*. A re-run that only re-attempts a row the previous run left open
   * does not count here — it shows up under `charged` / `failed`, so the log can never read as
   * though a month was billed twice.
   */
  created: number;
  /** Groups skipped because their row was already settled. */
  skipped: number;
  /** Card rows Cardcom accepted. */
  charged: number;
  /** Card rows Cardcom rejected. */
  failed: number;
  /** Σ `amount` over the rows this run wrote. */
  totalAmount: number;
  errors: string[];
  user?: { id: number; firstName: string; lastName: string } | null;
}

/** Query params for the server-side payment list (all optional, mirror the URL). */
export interface PaymentListParams {
  page?: string | number;
  rowsPerPage?: string | number;
  sortField?: string;
  sortOrder?: string;
  /** Inclusive 'YYYY-MM-DD' bounds on `chargeDate`. */
  startDate?: string;
  endDate?: string;
  /** 'YYYY-MM' — the billed month. */
  period?: string;
  /** Not a screen filter — it lets a customer card deep-link to that customer's charges. */
  customerId?: string | number;
  /** Free text over the snapshotted customer name. */
  searchTerm?: string;
  paymentMethod?: string;
  /** Comma-separated — the status filter is a multi-select. */
  status?: string;
}

export interface PaymentListResponse {
  rows: Payment[];
  count: number;
}

/**
 * What one nightly run did. Billing is fully automatic — there is no screen for this; the shape
 * is the job endpoint's response and the row kept in `PaymentRuns` for the audit trail.
 */
export interface RunBillingResult {
  /** Null on a dry run — nothing is persisted, so no run row is kept. */
  runId: number | null;
  /** The date the run billed for, 'YYYY-MM-DD' (normally today in Israel). */
  runDate: string;
  dryRun: boolean;
  /** Did today close a מל"ש cycle (the 27th)? */
  malashCycle: boolean;
  /** Card standing orders whose anniversary fell today. */
  cardCycles: number;
  examined: number;
  created: number;
  skipped: number;
  charged: number;
  failed: number;
  /** Failed card rows the retry sweep picked up. */
  retried: number;
  totalAmount: number;
  errors: string[];
}

// --- עזרי תאריך ---
// Israel is the only timezone this product runs in, and the billing date is a calendar date
// with no time of day. Everything here works on the local calendar, never on UTC instants.

const IL_TIME_ZONE = 'Asia/Jerusalem';

/** Local 'YYYY-MM-DD' in Israel for a given instant (defaults to now). */
export const israelDateString = (at: Date = new Date()): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: IL_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(at);

/** 'YYYY-MM-DD' → 'YYYY-MM'. Same rule as a credit's period. */
export const periodFromDateString = (date: string): string => date.slice(0, 7);

/** The period currently open in Israel — the month the job would bill today. */
export const currentIsraelPeriod = (at: Date = new Date()): string => periodFromDateString(israelDateString(at));

/** 'YYYY-MM' → 'YYYY-MM-DD' of the first day. */
export const firstDayOfPeriod = (period: string): string => `${period}-01`;

/** Days in a given month. `Date.UTC(y, m, 0)` rolls back from the 1st of the *next* month. */
export const daysInMonth = (year: number, month: number): number => new Date(Date.UTC(year, month, 0)).getUTCDate();

/**
 * 'YYYY-MM' → 'YYYY-MM-DD' of the last day. February and leap years fall out for free.
 */
export const lastDayOfPeriod = (period: string): string => {
  const [year, month] = period.split('-').map(Number);
  return toDateString(year, month, daysInMonth(year, month));
};

const pad = (n: number): string => String(n).padStart(2, '0');

/** Build 'YYYY-MM-DD' without going through a Date, so no timezone can shift the day. */
export const toDateString = (year: number, month: number, day: number): string =>
  `${year}-${pad(month)}-${pad(day)}`;

/** Parse 'YYYY-MM-DD' into its parts. No Date involved, so no timezone drift. */
export const parseDateString = (date: string): { year: number; month: number; day: number } => {
  const [year, month, day] = date.split('-').map(Number);
  return { year, month, day };
};

/** Shift a 'YYYY-MM-DD' by whole days. */
export const addDays = (date: string, days: number): string => {
  const { year, month, day } = parseDateString(date);
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return toDateString(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate());
};

/** Shift a 'YYYY-MM' by whole months. */
export const addMonthsToPeriod = (period: string, months: number): string => {
  const [year, month] = period.split('-').map(Number);
  const shifted = new Date(Date.UTC(year, month - 1 + months, 1));
  return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}`;
};

/**
 * The day a monthly cycle falls on inside a given month, clamped to the month's length.
 *
 * A subscription started on the 31st has no 31st in February, and a 27th always exists. Clamping
 * to the last day is the standard standing-order rule: the charge lands on the 28th (or 29th)
 * rather than slipping into March or being skipped altogether.
 */
export const cycleDayInMonth = (period: string, dayOfMonth: number): string => {
  const [year, month] = period.split('-').map(Number);
  return toDateString(year, month, Math.min(dayOfMonth, daysInMonth(year, month)));
};

// --- מחזורי חיוב ---

/**
 * The day of the month the מל"ש account is billed on. Everything shipped since the previous
 * 27th is settled together.
 */
export const MALASH_BILLING_DAY = 27;

/**
 * One billing cycle: the window of shipping dates it covers, and the date it is charged on.
 *
 * A cycle is deliberately **not** a calendar month. מל"ש runs 27th-to-26th, and each credit-card
 * standing order runs on its own subscription's anniversary. `period` is kept only as a
 * human-readable label for grouping and filtering — `start`/`end` are the truth.
 */
export interface BillingCycle {
  /** Inclusive 'YYYY-MM-DD' — the first shipping date in the cycle. */
  start: string;
  /** Inclusive 'YYYY-MM-DD' — the last shipping date in the cycle. */
  end: string;
  /** 'YYYY-MM-DD' — the day the charge is dated and raised. */
  chargeDate: string;
  /** 'YYYY-MM' of `chargeDate`; a label for the screen, never the key. */
  period: string;
}

/**
 * Build the cycle that closes on `chargeDate`, for a monthly cycle falling on `dayOfMonth`.
 *
 * The charge date **opens** the next cycle rather than closing this one: the window ends the day
 * before. That is what lets the job run on the morning of the 27th without racing the orders
 * generated that same day — the window it bills was already closed at midnight.
 */
export const cycleEndingOn = (chargeDate: string, dayOfMonth: number): BillingCycle => {
  const period = periodFromDateString(chargeDate);
  const previous = cycleDayInMonth(addMonthsToPeriod(period, -1), dayOfMonth);
  return { start: previous, end: addDays(chargeDate, -1), chargeDate, period };
};

/**
 * The מל"ש cycle charged on a given date. Returns null unless that date is the 27th — the
 * caller is the daily job asking "is today a מל"ש billing day?".
 */
export const malashCycleFor = (date: string): BillingCycle | null => {
  const { day } = parseDateString(date);
  return day === MALASH_BILLING_DAY ? cycleEndingOn(date, MALASH_BILLING_DAY) : null;
};

/**
 * The credit-card cycle charged on a given date for a standing order created on
 * `subscriptionStartDate`. Returns null when today is not that subscription's billing day.
 *
 * The billing day is the day-of-month the subscription was created on ("תאריך ההקמה"), clamped
 * to the length of the month being billed — so a subscription opened on the 31st is charged on
 * the 28th of February and on the 31st again in March.
 */
export const cardCycleFor = (date: string, subscriptionStartDate: string): BillingCycle | null => {
  const { day: billingDay } = parseDateString(subscriptionStartDate);
  const period = periodFromDateString(date);
  return cycleDayInMonth(period, billingDay) === date ? cycleEndingOn(date, billingDay) : null;
};

/** The day-of-month a subscription's standing order is charged on. */
export const cardBillingDayOf = (subscriptionStartDate: string): number =>
  parseDateString(subscriptionStartDate).day;

export const PERIOD_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;
export const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const isValidPeriod = (period: string): boolean => PERIOD_PATTERN.test(period);

/** One message for the Joi schema and the service, so the API never contradicts itself. */
export const INVALID_PERIOD_MESSAGE = 'חודש החיוב חייב להיות בפורמט YYYY-MM';

// --- כסף ---

/** Money is stored as DOUBLE; rounding at every boundary keeps 0.1 + 0.2 out of the totals. */
export const roundMoney = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

/**
 * How much of each credit this charge takes, in the order given.
 *
 * Credits are consumed oldest-first and only up to the charge's gross — a credit larger than the
 * month it lands in keeps its remainder for the next cycle rather than evaporating. The caller
 * persists the result as `PaymentCredits` rows, which is what stops the same credit being
 * counted again by a different cycle on a different day.
 */
export const consumeCredits = (
  available: readonly { id: number; remaining: number }[],
  grossAmount: number
): { creditId: number; amount: number }[] => {
  let left = roundMoney(Math.max(0, grossAmount));
  const taken: { creditId: number; amount: number }[] = [];

  for (const credit of available) {
    if (left <= 0) break;
    const take = roundMoney(Math.min(left, Math.max(0, credit.remaining)));
    if (take <= 0) continue;
    taken.push({ creditId: credit.id, amount: take });
    left = roundMoney(left - take);
  }
  return taken;
};
